import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { IyafWordmark } from "@/components/brand/IyafMark";
import { RegisterSoonButton } from "@/components/ui-iyaf/RegisterSoonButton";
import LanguageSelector from "@/components/site/LanguageSelector";

const armsMenuKeys = [
  { key: "hub", script: "hub", to: "/iyaf-hub", color: "#3663A8" },
  { key: "social", script: "social", to: "/iyaf-social", color: "#FF9F1C" },
  { key: "consult", script: "consult", to: "/iyaf-consult", color: "#8C8C8C" },
  { key: "venture", script: "venture", to: "/iyaf-venture", color: "#E01E84" },
];

const mainLinkKeys = [
  { key: "about", to: "/about" },
  { key: "model", to: "/model" },
  { key: "impact", to: "/impact" },
  { key: "stories", to: "/stories" },
  { key: "publications", to: "/publications" },
  { key: "engage", to: "/engage" },
];

export default function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [armsOpen, setArmsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent"}`}
    >
      <div className="container-iyaf flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center group">
          <IyafWordmark variant="main" size="md" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {mainLinkKeys.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "text-iyaf-blue" : "text-iyaf-ink/75 hover:text-iyaf-ink"}`
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
          <div className="relative" onMouseEnter={() => setArmsOpen(true)} onMouseLeave={() => setArmsOpen(false)}>
            <button data-testid="nav-arms" className="px-3 py-2 rounded-full text-sm font-medium text-iyaf-ink/75 hover:text-iyaf-ink flex items-center gap-1">
              {t("nav.ourArms")} <ChevronDown size={14} />
            </button>
            <AnimatePresence>
              {armsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute top-full end-0 pt-3 w-[560px] max-w-[calc(100vw-2rem)]"
                >
                  <div className="bg-white rounded-3xl border border-black/5 shadow-2xl shadow-black/5 p-4 grid grid-cols-2 gap-2">
                    {armsMenuKeys.map((a) => (
                      <Link key={a.to} to={a.to} data-testid={`mega-iyaf-${a.key}`} className="group p-4 rounded-2xl hover:bg-iyaf-paper transition-colors flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                          <IyafWordmark variant={a.script} size="sm" />
                          <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition" />
                        </div>
                        <p className="text-xs text-iyaf-ink/60 leading-relaxed">{t(`arms.${a.key}.tagline`)}</p>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block"><LanguageSelector /></div>
          <RegisterSoonButton
            label={t("nav.register")}
            icon={ArrowUpRight}
            testid="header-cta-register"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-iyaf-ink text-white text-sm font-semibold hover:bg-iyaf-blue transition-colors"
          />
          <button data-testid="header-cta-login" onClick={() => navigate("/login")} className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-iyaf-ink text-iyaf-ink text-sm font-semibold hover:bg-iyaf-ink hover:text-white transition-colors">
            {t("nav.memberPortal")}
          </button>
          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 -mr-2" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }} className="lg:hidden bg-white border-t border-black/5">
            <div className="container-iyaf py-6 flex flex-col gap-1" data-testid="mobile-drawer">
              {mainLinkKeys.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.key}`} className="py-3 text-lg font-display font-semibold border-b border-black/5">
                  {t(`nav.${l.key}`)}
                </NavLink>
              ))}
              <div className="pt-3">
                <p className="eyebrow text-iyaf-ink/45 mb-2">{t("nav.ourArms")}</p>
                {armsMenuKeys.map((a) => (
                  <Link key={a.to} to={a.to} onClick={() => setOpen(false)} className="py-2">
                    <IyafWordmark variant={a.script} size="sm" />
                  </Link>
                ))}
              </div>
              <div className="mt-5 mb-3">
                <LanguageSelector />
              </div>
              <div className="flex gap-3 mt-2">
                <RegisterSoonButton
                  label={t("nav.register")}
                  testid="mobile-cta-register"
                  className="flex-1 justify-center text-center py-3 rounded-full bg-iyaf-ink text-white font-semibold"
                />
                <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center py-3 rounded-full border-2 border-iyaf-ink font-semibold">{t("nav.portal")}</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
